import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Send, Check, Link2, Archive, ArchiveRestore } from "lucide-react";
import { Card, field, cfield, btn } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLES = {
  draft: "bg-white text-ink",
  sent: "bg-staxyellow text-ink",
  paid: "bg-staxcyan text-ink",
  archived: "bg-ink/10 text-ink/50",
};
const CUSTOM = "__custom__";

export const InvoicesTab = ({ auth }) => {
  const [invoices, setInvoices] = useState([]);
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_email: "", due_date: "", notes: "" });
  const [items, setItems] = useState([{ desc: "", qty: 1, price: "", custom: false }]);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => axios.get(`${API}/admin/invoices`, auth).then((r) => setInvoices(r.data)).catch(() => {});
  const loadServices = () => axios.get(`${API}/admin/invoice-services`, auth).then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadServices();
    axios.get(`${API}/leads`, auth).then((r) => setLeads(r.data.filter((l) => l.email || l.name))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItem = (i, k, v) => setItems((s) => s.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  const pickService = (i, value) => {
    if (value === CUSTOM) {
      setItems((s) => s.map((it, j) => (j === i ? { ...it, desc: "", custom: true } : it)));
      return;
    }
    const svc = services.find((s) => s.desc === value);
    setItems((s) => s.map((it, j) => (j === i
      ? { ...it, desc: value, custom: false, price: svc?.price != null ? String(svc.price) : it.price }
      : it)));
  };

  const pickLead = (id) => {
    const l = leads.find((x) => x.id === id);
    if (!l) return;
    setForm((f) => ({ ...f, client_name: l.name || f.client_name, client_email: l.email || f.client_email }));
  };

  const total = items.reduce((sum, i) => sum + (parseInt(i.qty) || 1) * (parseFloat(i.price) || 0), 0);

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { toast.error("Client name is required."); return; }
    const cleanItems = items.filter((i) => i.desc.trim()).map((i) => ({ desc: i.desc.trim(), qty: parseInt(i.qty) || 1, price: parseFloat(i.price) || 0 }));
    setBusy(true);
    try {
      await axios.post(`${API}/admin/invoices`, { ...form, items: cleanItems }, auth);
      // Save any new custom services so they show up in the dropdown next time
      const known = new Set(services.map((s) => s.desc));
      for (const i of items) {
        if (i.custom && i.desc.trim() && !known.has(i.desc.trim())) {
          axios.post(`${API}/admin/invoice-services`, { desc: i.desc.trim(), price: parseFloat(i.price) || null }, auth)
            .then(loadServices).catch(() => {});
        }
      }
      toast.success("Invoice created.");
      setForm({ client_name: "", client_email: "", due_date: "", notes: "" });
      setItems([{ desc: "", qty: 1, price: "", custom: false }]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create invoice."); }
    finally { setBusy(false); }
  };

  const send = async (id) => {
    try {
      await axios.post(`${API}/admin/invoices/${id}/send`, {}, auth);
      toast.success("Invoice emailed to client.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed."); }
  };

  const markPaid = async (id) => {
    try {
      await axios.patch(`${API}/admin/invoices/${id}`, { status: "paid" }, auth);
      load();
    } catch { toast.error("Update failed."); }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, auth);
      setInvoices((s) => s.filter((i) => i.id !== id));
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed."); }
  };

  const archive = async (id, archiveIt) => {
    try {
      await axios.patch(`${API}/admin/invoices/${id}`, { status: archiveIt ? "archived" : "draft" }, auth);
      toast.success(archiveIt ? "Invoice archived." : "Invoice restored to drafts.");
      load();
    } catch { toast.error("Update failed."); }
  };

  const archivedCount = invoices.filter((i) => i.status === "archived").length;
  const shown = invoices.filter((i) => showArchived || i.status !== "archived");

  const copyLink = (id) => {
    navigator.clipboard?.writeText(`${window.location.origin}/invoice/${id}`);
    toast.success("Invoice link copied.");
  };

  return (
    <div className="space-y-8" data-testid="invoices-tab">
      <Card title="New Invoice" color="bg-staxcyan" testid="invoice-create-card">
        <form onSubmit={create} className="space-y-3">
          <select data-testid="inv-client-from-leads" defaultValue="" onChange={(e) => pickLead(e.target.value)} className={field}>
            <option value="" disabled>Pick a client from your inquiries...</option>
            {leads.map((l) => (
              <option key={l.id} value={l.id}>{l.name || "Unnamed"}{l.email ? ` — ${l.email}` : ""}</option>
            ))}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="inv-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="Client name * (or type a new one)" className={field} />
            <input data-testid="inv-client-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              placeholder="Client email (to send it)" className={field} />
            <input data-testid="inv-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={field} />
            <input data-testid="inv-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes (payment terms, etc.)" className={field} />
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2">
                <select
                  data-testid={`inv-item-service-${i}`}
                  value={it.custom ? CUSTOM : it.desc}
                  onChange={(e) => pickService(i, e.target.value)}
                  className={`${cfield} flex-1`}
                >
                  <option value="">Choose a service...</option>
                  {services.map((s, idx) => (
                    <option key={idx} value={s.desc}>{s.desc}{s.price != null ? ` — $${s.price}` : ""}</option>
                  ))}
                  <option value={CUSTOM}>✎ Type a custom service...</option>
                </select>
                {it.custom && (
                  <input data-testid={`inv-item-desc-${i}`} value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)}
                    placeholder="Custom service name (saved for next time)" className={`${cfield} flex-1`} />
                )}
                <div className="flex gap-2">
                  <input data-testid={`inv-item-qty-${i}`} type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)}
                    className={`${cfield} w-20`} />
                  <input data-testid={`inv-item-price-${i}`} type="number" min="0" step="0.01" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)}
                    placeholder="$" className={`${cfield} w-28`} />
                  {items.length > 1 && (
                    <button type="button" data-testid={`inv-item-remove-${i}`} onClick={() => setItems((s) => s.filter((_, j) => j !== i))}
                      aria-label="Remove item" className="border-2 border-ink p-2 hover:bg-staxmagenta hover:text-white transition-colors">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button type="button" data-testid="inv-item-add" onClick={() => setItems((s) => [...s, { desc: "", qty: 1, price: "", custom: false }])}
              className={`${btn} bg-white text-ink`}>
              <Plus size={14} /> Add Line
            </button>
            <p className="font-display text-2xl">Total: <span data-testid="inv-total-preview">${total.toFixed(2)}</span></p>
          </div>
          <button data-testid="inv-create-button" type="submit" disabled={busy} className={`${btn} w-full bg-staxmagenta text-white`}>
            {busy ? "Creating..." : "Create Draft Invoice"}
          </button>
        </form>
      </Card>

      <Card
        title={`Invoices (${shown.length})`}
        color="bg-staxyellow"
        testid="invoices-list"
        action={archivedCount > 0 && (
          <button data-testid="invoices-show-archived" onClick={() => setShowArchived((s) => !s)} className={`${btn} bg-white text-ink`}>
            <Archive size={14} /> {showArchived ? "Hide archived" : `Archived (${archivedCount})`}
          </button>
        )}
      >
        {shown.length === 0 && <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No invoices yet.</p>}
        <div className="space-y-3">
          {shown.map((inv) => (
            <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-2 border-ink/15 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display uppercase text-lg leading-tight">{inv.number} — {inv.client_name}</p>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-ink/50">
                    ${(inv.total ?? 0).toFixed(2)}{inv.due_date ? ` · due ${inv.due_date}` : ""}{inv.client_email ? ` · ${inv.client_email}` : ""}
                  </p>
                </div>
                <span className={`border-2 border-ink px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[inv.status]}`}>
                  {inv.status}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid={`invoice-copy-${inv.id}`} onClick={() => copyLink(inv.id)} className={`${btn} bg-white text-ink`}>
                  <Link2 size={14} /> Copy Link
                </button>
                {inv.client_email && inv.status !== "paid" && (
                  <button data-testid={`invoice-send-${inv.id}`} onClick={() => send(inv.id)} className={`${btn} bg-staxcyan text-ink`}>
                    <Send size={14} /> {inv.status === "sent" ? "Resend" : "Email Invoice"}
                  </button>
                )}
                {inv.status === "sent" && (
                  <button data-testid={`invoice-paid-${inv.id}`} onClick={() => markPaid(inv.id)} className={`${btn} bg-ink text-white`}>
                    <Check size={14} /> Mark Paid
                  </button>
                )}
                {inv.status === "draft" && (
                  <button data-testid={`invoice-delete-${inv.id}`} onClick={() => del(inv.id)} className={`${btn} bg-white text-ink hover:bg-staxmagenta hover:text-white`}>
                    <Trash2 size={14} /> Delete
                  </button>
                )}
                {inv.status === "archived" ? (
                  <button data-testid={`invoice-restore-${inv.id}`} onClick={() => archive(inv.id, false)} className={`${btn} bg-white text-ink`}>
                    <ArchiveRestore size={14} /> Restore
                  </button>
                ) : (
                  <button data-testid={`invoice-archive-${inv.id}`} onClick={() => archive(inv.id, true)} className={`${btn} bg-white text-ink`}>
                    <Archive size={14} /> Archive
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
